import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import Tooltip from './Tooltip'
import useModalEnter from '../hooks/useModalEnter'

// Inside-loader duration: modal open + Profile / Acc history tab clicks
const INSIDE_LOADER_MS = 1000

// BootstrapVue-style incrementing component ids (__BVID__1490 etc.) — same
// pattern as frontend/src/components/modals/ChangePasswordModal.jsx
let bvidCounter = 1489

function MoreModal({ show, onHide, userData, onChangePassword, onUserLock, onEditProfile }) {
  const bvid = useRef(`__BVID__${++bvidCounter}`).current
  // BootstrapVue tab component ids (tabs container + one per pane)
  const tabIds = useRef({
    tabs: `__BVID__${++bvidCounter}`,
    profile: `__BVID__${++bvidCounter}`,
    password: `__BVID__${++bvidCounter}`,
    lock: `__BVID__${++bvidCounter}`,
    history: `__BVID__${++bvidCounter}`,
    edit: `__BVID__${++bvidCounter}`,
  }).current
  const [activeTab, setActiveTab] = useState('profile')

  // Inside loader — 1s spinner over the body each time the modal opens AND
  // on every click of the Profile / Acc history tabs (the two that load data
  // in the reference), even when that tab is already the active one (user,
  // 2026-09-18). The other tabs (C Pass, Lock, Edit Profile) are plain forms
  // and switch instantly.
  const [insideLoading, setInsideLoading] = useState(false)
  const loaderTimer = useRef(null)
  const runInsideLoader = () => {
    clearTimeout(loaderTimer.current)
    setInsideLoading(true)
    loaderTimer.current = setTimeout(() => setInsideLoading(false), INSIDE_LOADER_MS)
  }
  useEffect(() => {
    if (show) runInsideLoader()
    return () => clearTimeout(loaderTimer.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])
  const LOADER_TABS = ['profile', 'history']
  const switchTab = (tab) => {
    setActiveTab(tab)
    // Leaving a tab throws away whatever was typed on it, so the next tab —
    // and this one, on the way back — always opens blank (user, 2026-09-21).
    resetForms()
    if (LOADER_TABS.includes(tab)) runInsideLoader()
  }

  // BootstrapVue marks the body while a modal is open
  useEffect(() => {
    if (!show) return
    document.body.classList.add('modal-open')
    document.body.setAttribute('data-modal-open-count', '1')
    return () => {
      document.body.classList.remove('modal-open')
      document.body.removeAttribute('data-modal-open-count')
    }
  }, [show])

  // Change Password
  const [cpPassword, setCpPassword] = useState('')
  const [cpConfirm, setCpConfirm] = useState('')
  const [cpMpassword, setCpMpassword] = useState('')
  const [cpErrors, setCpErrors] = useState({})

  // Reference (vee-validate) texts — validated live on change/blur/focus-out
  const cpValidate = (name, value, all) => {
    switch (name) {
      case 'password':
        if (!value) return 'The Password field is required'
        if (value.length < 8) return 'The Password field must be at least 8 characters'
        return ''
      case 'confirm':
        if (!value) return 'The Confirm Password field is required'
        if (value.length < 8) return 'The Confirm Password field must be at least 8 characters'
        if (value !== all.password) return 'The Confirm Password confirmation does not match'
        return ''
      case 'mpassword':
        if (!value) return 'The Transaction Code field is required'
        return ''
      default:
        return ''
    }
  }

  const cpField = (name, value) => {
    const all = { password: cpPassword, confirm: cpConfirm, mpassword: cpMpassword, [name]: value }
    if (name === 'password') setCpPassword(value)
    if (name === 'confirm') setCpConfirm(value)
    if (name === 'mpassword') setCpMpassword(value)
    setCpErrors((prev) => {
      const next = { ...prev, [name]: cpValidate(name, value, all) }
      // password change can fix/break the confirm match
      if (name === 'password' && all.confirm) next.confirm = cpValidate('confirm', all.confirm, all)
      return next
    })
  }

  const cpBlur = (name, value) => {
    const all = { password: cpPassword, confirm: cpConfirm, mpassword: cpMpassword }
    setCpErrors((prev) => ({ ...prev, [name]: cpValidate(name, value, all) }))
  }

  // User Lock
  const [betLock, setBetLock] = useState(false)
  const [userLock, setUserLock] = useState(false)
  const [lockMpassword, setLockMpassword] = useState('')
  const [lockMpassError, setLockMpassError] = useState(null)

  // Edit Profile
  const [fname, setFname] = useState('')
  const [changePwdLock, setChangePwdLock] = useState(false)
  const [favMaster, setFavMaster] = useState(false)
  const [editMpass, setEditMpass] = useState('')
  const [editMpassError, setEditMpassError] = useState(null)

  // Every form in this panel back to the state it has when the modal opens:
  // typed text cleared, errors dropped, toggles re-read from the account.
  // Used on open AND on every tab switch (user, 2026-09-21) — a password or a
  // transaction password half-typed on one tab must not still be sitting there
  // when the operator comes back to it from another tab.
  const resetForms = () => {
    setCpPassword('')
    setCpConfirm('')
    setCpMpassword('')
    setCpErrors({})
    setBetLock(!!(userData?.bet_lock ?? userData?.bet_locked))
    setUserLock(!!(userData?.user_lock ?? (userData?.status === 'InActive')))
    setLockMpassword('')
    setLockMpassError(null)
    setFname(userData?.fullname || userData?.full_name || userData?.fullName || '')
    setChangePwdLock(false)
    setFavMaster(false)
    setEditMpass('')
    setEditMpassError(null)
  }

  useEffect(() => {
    if (show && userData) {
      setActiveTab('profile')
      resetForms()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, userData])

  const { modalClass, backdropClass, onBackdropClick } = useModalEnter(onHide, show)

  if (!show || !userData) return null

  const initial = (userData.username || '?').charAt(0).toUpperCase()
  // Same keys, same order as the Active Users / Users list rows (all-details
  // API) — the old keys don't exist there, so Credit pts / Client P/L read 0.
  const creditRef = Number(userData.credit ?? userData.credit_ref ?? userData.cash_received) || 0
  const pts = Number(userData.all_point ?? userData.inr_balance ?? userData.cash) || 0
  const profitLoss = Number(userData.settlement_points ?? userData.profit_loss) || 0
  // Stored negative (liability); shown as magnitude like the list column.
  const exposure = Math.abs(Number(userData.exposure ?? userData.total_exposure)) || 0
  // Partnership rows are staff-only; a player's card stays blank (user, 2026-09-18).
  const isStaffAccount = String(userData.user_type ?? userData.role ?? 'USER').toUpperCase() !== 'USER'
  const casinoPts = Number(userData.casino_pts) || 0
  const sportsPts = Number(userData.sports_pts) || 0
  const thirdPartyPts = Number(userData.third_party_pts) || 0
  // Reference shows DD/MM/YYYY HH:mm:ss (e.g. 20/07/2026 03:41:26)
  const rawCreated = userData.created_at || userData.createdAt || ''
  const createdDate = (() => {
    if (!rawCreated) return ''
    const d = new Date(rawCreated)
    if (isNaN(d)) return rawCreated
    const p = (n) => String(n).padStart(2, '0')
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
  })()
  const fmt = (v) => Number(v).toLocaleString()

  const handleChangePassword = (e) => {
    e.preventDefault()
    const all = { password: cpPassword, confirm: cpConfirm, mpassword: cpMpassword }
    const errs = {
      password: cpValidate('password', cpPassword, all),
      confirm: cpValidate('confirm', cpConfirm, all),
      mpassword: cpValidate('mpassword', cpMpassword, all),
    }
    setCpErrors(errs)
    if (errs.password || errs.confirm || errs.mpassword) return
    onChangePassword?.({ newPassword: cpPassword, transactionPassword: cpMpassword })
  }

  const handleUserLock = (e) => {
    e.preventDefault()
    const err = lockMpassword ? '' : 'The Transaction Code field is required'
    setLockMpassError(err)
    if (err) return
    onUserLock?.({ betLock, userLock, transactionPassword: lockMpassword })
  }

  const handleEditProfile = (e) => {
    e.preventDefault()
    const err = editMpass ? '' : 'The Transaction Code field is required'
    setEditMpassError(err)
    if (err) return
    onEditProfile?.({ fullName: fname, changePasswordLock: changePwdLock, favoriteMaster: favMaster, transactionPassword: editMpass })
  }

  // Reference (BootstrapVue) portals the modal to the end of <body> and adds
  // body.modal-open — nesting it inside page-content breaks stacking/scroll
  return createPortal(
    <>
      <div id={`${bvid}___BV_modal_outer_`} style={{ position: 'absolute', zIndex: 1040 }}>
        <div id={bvid} role="dialog" aria-describedby={`${bvid}___BV_modal_body_`} className={modalClass} aria-modal="true" onClick={onBackdropClick} style={{ display: 'block' }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <span tabIndex="0"></span>
            <div id={`${bvid}___BV_modal_content_`} className="modal-content" tabIndex="-1">
              <header id={`${bvid}___BV_modal_header_`} className="modal-header bg-primary">
                <h5 className="modal-title text-uppercase text-white">{userData.username}</h5>
                <button type="button" data-dismiss="modal" className="close text-white" onClick={onHide}>×</button>
              </header>
              <div id={`${bvid}___BV_modal_body_`} className="modal-body theme-bg">
                {insideLoading && (
                  <div className="inside-loader-overlay"><i className="fas fa-spinner fa-spin"></i></div>
                )}
                {/* Reference behavior: content stays rendered, loader overlays it */}
                <div className="tabs" id={tabIds.tabs}>
                  <div className="">
                    <ul role="tablist" className="nav nav-tabs" id={`${tabIds.tabs}__BV_tab_controls_`}>
                      <li role="presentation" className="nav-item">
                        <a role="tab" tabIndex={activeTab === 'profile' ? undefined : -1} aria-selected={activeTab === 'profile'} aria-setsize="5" aria-posinset="1" href="#" target="_self"
                          className={`nav-link ${activeTab === 'profile' ? 'active tab-bg-primary' : ''}`}
                          id={`${tabIds.profile}___BV_tab_button__`} aria-controls={tabIds.profile}
                          onClick={(e) => { e.preventDefault(); switchTab('profile') }}>
                          <span>Profile</span>
                        </a>
                      </li>
                      <li role="presentation" className="nav-item">
                        <a role="tab" tabIndex={activeTab === 'password' ? undefined : -1} aria-selected={activeTab === 'password'} aria-setsize="5" aria-posinset="2" href="#" target="_self"
                          className={`nav-link ${activeTab === 'password' ? 'active tab-bg-primary' : ''}`}
                          id={`${tabIds.password}___BV_tab_button__`} aria-controls={tabIds.password}
                          onClick={(e) => { e.preventDefault(); switchTab('password') }}>
                          <span className="d-inline-block d-sm-none"> C Pass </span>
                          <span className="d-none d-sm-inline-block">Change Password</span>
                        </a>
                      </li>
                      <li role="presentation" className="nav-item">
                        <a role="tab" tabIndex={activeTab === 'lock' ? undefined : -1} aria-selected={activeTab === 'lock'} aria-setsize="5" aria-posinset="3" href="#" target="_self"
                          className={`nav-link ${activeTab === 'lock' ? 'active tab-bg-primary' : ''}`}
                          id={`${tabIds.lock}___BV_tab_button__`} aria-controls={tabIds.lock}
                          onClick={(e) => { e.preventDefault(); switchTab('lock') }}>
                          <span className="d-inline-block d-sm-none"> Lock </span>
                          <span className="d-none d-sm-inline-block">User lock</span>
                        </a>
                      </li>
                      <li role="presentation" className="nav-item">
                        <a role="tab" tabIndex={activeTab === 'history' ? undefined : -1} aria-selected={activeTab === 'history'} aria-setsize="5" aria-posinset="4" href="#" target="_self"
                          className={`nav-link ${activeTab === 'history' ? 'active tab-bg-primary' : ''}`}
                          id={`${tabIds.history}___BV_tab_button__`} aria-controls={tabIds.history}
                          onClick={(e) => { e.preventDefault(); switchTab('history') }}>
                          <span className="d-inline-block d-sm-none">Acc history</span>
                          <span className="d-none d-sm-inline-block">Account history</span>
                        </a>
                      </li>
                      <li role="presentation" className="nav-item">
                        <a role="tab" tabIndex={activeTab === 'edit' ? undefined : -1} aria-selected={activeTab === 'edit'} aria-setsize="5" aria-posinset="5" href="#" target="_self"
                          className={`nav-link ${activeTab === 'edit' ? 'active tab-bg-primary' : ''}`}
                          id={`${tabIds.edit}___BV_tab_button__`} aria-controls={tabIds.edit}
                          onClick={(e) => { e.preventDefault(); switchTab('edit') }}>
                          <span className="d-inline-block d-sm-none">Edit Profile</span>
                          <span className="d-none d-sm-inline-block">Edit Profile</span>
                        </a>
                      </li>
                    </ul>
                  </div>
                  <div className="tab-content text-muted" id={`${tabIds.tabs}__BV_tab_container_`}>
                    {/* Profile */}
                    {/* Reference: while the inside loader runs, the Profile pane stays
                        empty (tabs bar visible) — its content fills in after */}
                    <div role="tabpanel" aria-hidden={activeTab !== 'profile'} className={`tab-pane ${activeTab === 'profile' ? 'active' : ''}`} id={tabIds.profile} aria-labelledby={`${tabIds.profile}___BV_tab_button__`} style={activeTab === 'profile' ? {} : { display: 'none' }}>
                      {!insideLoading && (
                      <div className="row">
                        <div className="col-xl-6">
                          <div className="card text-center">
                            <div className="card-body p-2">
                              <div className="avatar-sm mx-auto mb-1">
                                <span className="avatar-title rounded-circle bg-soft-primary text-primary font-size-16 text-uppercase">{initial}</span>
                              </div>
                              <h5 className="font-size-15">
                                <a href="javascript: void(0);" className="text-dark">{userData.username}</a>
                              </h5>
                              <p className="text-muted mb-1">{userData.fullname || userData.full_name || userData.fullName || userData.username}</p>
                            </div>
                            <div className="card-footer bg-transparent border-top">
                              <div className="contact-links d-flex font-size-20">
                                {/* Reference: v-b-tooltip.hover.top on both links — the dark
                                    bubble ABOVE the icon, phone number on the first, "City: <city>"
                                    on the second (the capture shows "City: " even with no city,
                                    so the prefix always prints). A bare `title` attr only gave the
                                    browser's own delayed tooltip, so it is gone: BootstrapVue
                                    strips it for the same reason. */}
                                <div className="flex-fill">
                                  {/* The API row calls it phone_number (see /admin/wallets); reading
                                      `mobile` here meant the bubble never had anything to show, which
                                      is why it looked like it never appeared. Bare number, as on the
                                      reference — no "Mobile Number:" prefix (user, 2026-09-16). */}
                                  <Tooltip text={userData.phone_number || userData.mobile || ''} placement="top">
                                    <a href="javascript: void(0);"><i className="bx bx-phone-call"></i></a>
                                  </Tooltip>
                                </div>
                                <div className="flex-fill">
                                  <Tooltip text={`City: ${userData.city || ''}`} placement="top">
                                    <a href="javascript: void(0);"><i className="bx bxs-city"></i></a>
                                  </Tooltip>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="card personalinfo-card">
                            <div className="card-body">
                              <h4 className="card-title mb-4">Partnership Information</h4>
                              <div className="table-responsive mb-0">
                                <table className="table">
                                  <tbody>
                                    {/* Partnership rows for staff only (Our Part = its User
                                        Rate / percentage). Remark is ALWAYS printed on those
                                        cards and falls back to "NA" — the reference DOM (user,
                                        2026-09-21) shows `Remark: NA` on an account with none,
                                        and nothing in users/staff/wallets stores one today.
                                        Ours only drew the row when a value existed, so the
                                        table was one row short. */}
                                    {isStaffAccount ? (
                                      <>
                                        <tr><th scope="row" className="br-0">Partnership Name:</th><td className="br-0">{userData.partnership_name || 'Point'}</td></tr>
                                        <tr><th scope="row" className="br-0">User Part:</th><td className="br-0">{userData.user_part ?? 0}</td></tr>
                                        <tr><th scope="row" className="br-0">Our Part:</th><td className="br-0">{userData.percentage ?? 0}</td></tr>
                                        <tr><th scope="row" className="br-0">Remark:</th><td className="br-0">{userData.remark || 'NA'}</td></tr>
                                      </>
                                    ) : (
                                      userData.remark ? (
                                        <tr><th scope="row" className="br-0">Remark:</th><td className="br-0">{userData.remark}</td></tr>
                                      ) : null
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-xl-6">
                          <div className="card">
                            <div className="card-body">
                              <h4 className="card-title mb-4">Additional Information</h4>
                              <div className="table-responsive mb-0">
                                <table className="table">
                                  <tbody>
                                    <tr><th scope="row" className="br-0">User Name:</th><td className="br-0">{userData.username}</td></tr>
                                    <tr><th scope="row" className="br-0">Full Name:</th><td className="br-0">{userData.fullname || userData.full_name || userData.fullName || ''}</td></tr>
                                    <tr><th scope="row" className="br-0">Mobile Number:</th><td className="br-0">{userData.phone_number || userData.mobile || ''}</td></tr>
                                    <tr><th scope="row" className="br-0">City:</th><td className="br-0">{userData.city || ''}</td></tr>
                                    <tr><th scope="row" className="br-0">Credit pts:</th><td className="br-0">{fmt(creditRef)}</td></tr>
                                    <tr><th scope="row" className="br-0">pts:</th><td className="br-0"><span>{fmt(pts)}</span></td></tr>
                                    <tr><th scope="row" className="br-0">Client P/L:</th><td className="br-0">{fmt(profitLoss)}</td></tr>
                                    <tr><th scope="row" className="br-0">Exposure:</th><td className="br-0">{fmt(exposure)}</td></tr>
                                    <tr><th scope="row" className="br-0">Casino pts:</th><td className="br-0">{fmt(casinoPts)}</td></tr>
                                    <tr><th scope="row" className="br-0">Sports pts:</th><td className="br-0">{fmt(sportsPts)}</td></tr>
                                    <tr><th scope="row" className="br-0">Third Party pts:</th><td className="br-0">{fmt(thirdPartyPts)}</td></tr>
                                    <tr><th scope="row" className="br-0">Created Date :</th><td className="br-0"><span>{createdDate}</span></td></tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      )}
                    </div>

                    {/* Change Password */}
                    <div role="tabpanel" aria-hidden={activeTab !== 'password'} className={`tab-pane ${activeTab === 'password' ? 'active' : ''}`} id={tabIds.password} aria-labelledby={`${tabIds.password}___BV_tab_button__`} style={activeTab === 'password' ? {} : { display: 'none' }}>
                      <form data-vv-scope="UserChangePassword" method="post" onSubmit={handleChangePassword}>
                        <div className="form-group row">
                          <label className="col-form-label col-4">Password</label>
                          <div className="col-8 form-group-feedback form-group-feedback-right">
                            <input placeholder="Password" type="password" name="userchangepasswordpassword" data-vv-as="Password" className={`form-control${cpErrors.password ? ' is-invalid' : ''}`} aria-required="true" aria-invalid={cpErrors.password ? 'true' : 'false'} value={cpPassword} onChange={(e) => cpField('password', e.target.value)} onBlur={(e) => cpBlur('password', e.target.value)} />
                            {cpErrors.password && <small className="error">{cpErrors.password}</small>}
                          </div>
                        </div>
                        <div className="form-group row">
                          <label className="col-form-label col-4">Confirm Password</label>
                          <div className="col-8 form-group-feedback form-group-feedback-right">
                            <input placeholder="Confirm Password" type="password" name="userchangepasswordcpassword" data-vv-as="Confirm Password" className={`form-control${cpErrors.confirm ? ' is-invalid' : ''}`} aria-required="true" aria-invalid={cpErrors.confirm ? 'true' : 'false'} value={cpConfirm} onChange={(e) => cpField('confirm', e.target.value)} onBlur={(e) => cpBlur('confirm', e.target.value)} />
                            {cpErrors.confirm && <small className="error">{cpErrors.confirm}</small>}
                          </div>
                        </div>
                        <div className="form-group row">
                          <label className="col-form-label col-4">Transaction Code</label>
                          <div className="col-8 form-group-feedback form-group-feedback-right">
                            <input placeholder="Transaction Code" type="password" name="userchangepasswordmpassword" data-vv-as="Transaction Code" className={`form-control${cpErrors.mpassword ? ' is-invalid' : ''}`} aria-required="true" aria-invalid={cpErrors.mpassword ? 'true' : 'false'} value={cpMpassword} onChange={(e) => cpField('mpassword', e.target.value)} onBlur={(e) => cpBlur('mpassword', e.target.value)} />
                            {cpErrors.mpassword && <small className="error">{cpErrors.mpassword}</small>}
                          </div>
                        </div>
                        <div className="form-group row">
                          <div className="col-12 text-right">
                            <button type="submit" className="btn btn-primary">submit<i className="fas fa-sign-in-alt ml-1"></i></button>
                          </div>
                        </div>
                      </form>
                    </div>

                    {/* User Lock */}
                    <div role="tabpanel" aria-hidden={activeTab !== 'lock'} className={`tab-pane ${activeTab === 'lock' ? 'active' : ''}`} id={tabIds.lock} aria-labelledby={`${tabIds.lock}___BV_tab_button__`} style={activeTab === 'lock' ? {} : { display: 'none' }}>
                      <form data-vv-scope="UserLock" method="post" onSubmit={handleUserLock}>
                        <div className="form-group row">
                          <label className="col-form-label col-4">Bet lock</label>
                          <div className="mb-1 custom-control custom-switch">
                            <input type="checkbox" className="custom-control-input" id="betLockSwitch" checked={betLock} onChange={(e) => setBetLock(e.target.checked)} />
                            <label className="custom-control-label" htmlFor="betLockSwitch"></label>
                          </div>
                        </div>
                        <div className="form-group row">
                          <label className="col-form-label col-4">User lock</label>
                          <div className="mb-1 custom-control custom-switch">
                            <input type="checkbox" className="custom-control-input" id="userLockSwitch" checked={userLock} onChange={(e) => setUserLock(e.target.checked)} />
                            <label className="custom-control-label" htmlFor="userLockSwitch"></label>
                          </div>
                        </div>
                        <div className="form-group row">
                          <label className="col-form-label col-4">Transaction Code</label>
                          <div className="col-8 form-group-feedback-right pl-0">
                            <input placeholder="Transaction Code" type="password" name="UserLockMpassword" className={`form-control${lockMpassError ? ' is-invalid' : ''}`} aria-required="true" aria-invalid={lockMpassError ? 'true' : 'false'} value={lockMpassword} onChange={(e) => { setLockMpassword(e.target.value); setLockMpassError(e.target.value ? '' : 'The Transaction Code field is required') }} onBlur={(e) => setLockMpassError(e.target.value ? '' : 'The Transaction Code field is required')} />
                          </div>
                        </div>
                        <div className="form-group row">
                          <div className="col-12 text-right">
                            <button type="submit" className="btn btn-primary">submit<i className="fas fa-sign-in-alt ml-1"></i></button>
                          </div>
                        </div>
                      </form>
                    </div>

                    {/* Account History */}
                    <div role="tabpanel" aria-hidden={activeTab !== 'history'} className={`tab-pane ${activeTab === 'history' ? 'active' : ''}`} id={tabIds.history} aria-labelledby={`${tabIds.history}___BV_tab_button__`} style={activeTab === 'history' ? {} : { display: 'none' }}>
                      <div className="table-responsive">
                        <div className="table no-footer table-responsive-sm">
                          <table id="eventsListTbl" role="table" aria-busy="false" aria-colcount="5" className="table b-table">
                            <thead role="rowgroup">
                              <tr role="row">
                                <th role="columnheader" scope="col" aria-colindex="1"><div>Super User</div></th>
                                <th role="columnheader" scope="col" aria-colindex="2"><div>User</div></th>
                                <th role="columnheader" scope="col" aria-colindex="3"><div>Transfer From</div></th>
                                <th role="columnheader" scope="col" aria-colindex="4" className="text-right"><div>Amount</div></th>
                                <th role="columnheader" scope="col" aria-colindex="5"><div>Date</div></th>
                              </tr>
                            </thead>
                            <tbody role="rowgroup">
                              <tr role="row" className="b-table-empty-row">
                                <td colSpan="5" role="cell">
                                  <div role="alert" aria-live="polite">
                                    <div className="text-center my-2">There are no records to show</div>
                                  </div>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    {/* Edit Profile */}
                    <div role="tabpanel" aria-hidden={activeTab !== 'edit'} className={`tab-pane ${activeTab === 'edit' ? 'active' : ''}`} id={tabIds.edit} aria-labelledby={`${tabIds.edit}___BV_tab_button__`} style={activeTab === 'edit' ? {} : { display: 'none' }}>
                      <form data-vv-scope="editprofile" method="post" onSubmit={handleEditProfile}>
                        <div className="form-group row">
                          <label className="col-form-label col-4">Full Name</label>
                          <div className="col-8 form-group-feedback-right pl-0">
                            <input placeholder="Full Name" type="text" name="fname" className="form-control" value={fname} onChange={(e) => setFname(e.target.value)} />
                          </div>
                        </div>
                        <div className="form-group row align-items-center">
                          <label className="col-form-label col-4">Change Password Lock</label>
                          <div className="mb-1 custom-control custom-switch">
                            <input type="checkbox" className="custom-control-input" id="changePwdLockSwitch" checked={changePwdLock} onChange={(e) => setChangePwdLock(e.target.checked)} />
                            <label className="custom-control-label" htmlFor="changePwdLockSwitch"></label>
                          </div>
                        </div>
                        <div className="form-group row align-items-center">
                          <label className="col-form-label col-4">Favorite Master</label>
                          <div className="mb-1 custom-control custom-switch">
                            <input type="checkbox" className="custom-control-input" id="favMasterSwitch" checked={favMaster} onChange={(e) => setFavMaster(e.target.checked)} />
                            <label className="custom-control-label" htmlFor="favMasterSwitch"></label>
                          </div>
                        </div>
                        <div className="form-group row">
                          <label className="col-form-label col-4">Transaction Code</label>
                          <div className="col-8 form-group-feedback-right pl-0">
                            <input placeholder="Transaction Code" type="password" name="mpass" className={`form-control${editMpassError ? ' is-invalid' : ''}`} aria-required="true" aria-invalid={editMpassError ? 'true' : 'false'} value={editMpass} onChange={(e) => { setEditMpass(e.target.value); setEditMpassError(e.target.value ? '' : 'The Transaction Code field is required') }} onBlur={(e) => setEditMpassError(e.target.value ? '' : 'The Transaction Code field is required')} />
                          </div>
                        </div>
                        <div className="form-group row">
                          <div className="col-12 text-right">
                            <button type="submit" className="btn btn-primary">submit<i className="fas fa-sign-in-alt ml-1"></i></button>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <span tabIndex="0"></span>
          </div>
        </div>
        <div id={`${bvid}___BV_modal_backdrop_`} className={backdropClass}></div>
      </div>
    </>,
    document.body
  )
}

export default MoreModal
